import React from 'react'

import { AppBar, Box, Toolbar } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CollegeCompareAvatars } from '@scoir/college-compare'
import { ErrorBoundary } from '@scoir/error-reporter'
import { HeightObserver } from '@scoir/height-observer'
import cn from 'classnames'
import { size } from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import { useOpenClose } from 'common/use-open-close'
import { useResponsiveBreakpoint } from 'common/use-responsive-breakpoint'
import { AuthShieldHide, useIsAuthed } from 'components/AuthShield'
import { FEATURE_NAMES } from 'components/ConfiguredDynamicFeatureProvider/features'
import DynamicFeature from 'components/DynamicFeatureProvider/DynamicFeature'

import { TOP_NAVIGATION_HEIGHT_ID } from '..'
import PageTabs from './PageTabs'
import DrawerSidebar, { DrawerTrigger } from './Sidebar/Drawer'
import FloatingSidebar from './Sidebar/Floating'
import SkipNav from './SkipNav'
import StudentAwesomeBar from './StudentAwesomeBar'
import ApplyWithScoir from './components/ApplyWithScoir'
import CreateAccount from './components/CreateAccount'
import LoginButton from './components/LoginButton'
import logo from './scoir-blue-logo.svg'

const useStyles = makeStyles((theme) => {
  return {
    // Padding/Margin top is hard coded off these values (ie. Discover, Dashboard, Messages, Profile)
    navWrapper: {
      height: '76px',
      [theme.breakpoints.down(960)]: {
        height: '64px',
      },
    },
    wrapper: {
      zIndex: 10,
      position: 'relative',

      '& .rccompare-avatars-comp': {
        position: 'static',
      },
    },
    appBar: {
      zIndex: 11,
      position: 'fixed',
      background: 'white',
      borderBottom: '1px solid rgba(0,0,0,0.12)',
    },
  }
})

/**
 * AppBar top navigation for the student app
 * @param {Object} props
 * @prop {String} skipNavId
 * @returns Navigation Component
 */
const Navigation = ({ location, skipNavId, hasCollegesToCompare }) => {
  const isSm = useResponsiveBreakpoint('sm')
  const isXs = useResponsiveBreakpoint('xs')
  const isBelow960 = useResponsiveBreakpoint(960)
  const isBelow1190 = useResponsiveBreakpoint(1190)
  const classes = useStyles()
  const sidebarControls = useOpenClose(false)
  const isAuthed = useIsAuthed()
  const shouldShowApplyWithScoir = isAuthed && !isBelow1190
  const shouldShowPageTabs = !isBelow960 || isAuthed

  return (
    <div
      id="main-nav"
      className={cn(classes.wrapper, 'e2e-student-header-navigation-comp')}
    >
      <div className={classes.navWrapper}>
        <AppBar
          color="default"
          position="fixed"
          elevation={0}
          aria-label="Scoir main navigation"
          className={classes.appBar}
        >
          <HeightObserver heightId={TOP_NAVIGATION_HEIGHT_ID}>
            <Box px={3}>
              <Toolbar disableGutters>
                <Box
                  width="100%"
                  flexGrow={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box
                    flexGrow={1}
                    display="flex"
                    width="100%"
                    alignItems="center"
                  >
                    <Box flexShrink={1}>
                      <SkipNav skipNavId={skipNavId} />
                      <Link to={`${import.meta.env.BASE_URL}/dashboard`}>
                        <Box
                          mr={3}
                          my={'20.5px'}
                        >
                          <img
                            alt="scoir logo"
                            src={logo}
                            width="100px"
                            title="Navigate to Scoir Dashboard"
                            className="student-header-logo"
                          />
                        </Box>
                      </Link>
                    </Box>
                    {!isXs && (
                      <Box flexGrow={1}>
                        <ErrorBoundary>
                          <StudentAwesomeBar />
                        </ErrorBoundary>
                      </Box>
                    )}
                  </Box>
                  {isSm ? (
                    <ErrorBoundary>
                      <DrawerTrigger {...sidebarControls} />
                    </ErrorBoundary>
                  ) : (
                    <React.Fragment>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {!shouldShowPageTabs ? null : (
                          <React.Fragment>
                            <ErrorBoundary>
                              <PageTabs pathname={location.pathname} />
                            </ErrorBoundary>
                            <ErrorBoundary>
                              <AuthShieldHide hideUnauthed>
                                <FloatingSidebar
                                  {...sidebarControls}
                                  pathname={location.pathname}
                                  queryParams={location.search}
                                />
                              </AuthShieldHide>
                            </ErrorBoundary>
                          </React.Fragment>
                        )}
                        {isSm ? null : <LoginButton />}
                        <DynamicFeature name={FEATURE_NAMES.MyColleges}>
                          {!shouldShowApplyWithScoir ? null : <ApplyWithScoir />}
                        </DynamicFeature>
                      </Box>
                      <AuthShieldHide hideAuthed>
                        <CreateAccount />
                      </AuthShieldHide>
                      {shouldShowPageTabs ? null : (
                        <ErrorBoundary>
                          <Box ml={1}>
                            <DrawerTrigger {...sidebarControls} />
                            <DrawerSidebar
                              {...sidebarControls}
                              pathname={location.pathname}
                              queryParams={location.search}
                            />
                          </Box>
                        </ErrorBoundary>
                      )}
                    </React.Fragment>
                  )}
                </Box>
              </Toolbar>
            </Box>
          </HeightObserver>
        </AppBar>
      </div>
      {!isSm ? null : (
        <ErrorBoundary>
          <DrawerSidebar
            {...sidebarControls}
            pathname={location.pathname}
            queryParams={location.search}
          />
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        <React.Fragment>
          {!hasCollegesToCompare ? null : (
            <Box
              position="fixed"
              top="45%"
              right="0"
            >
              <CollegeCompareAvatars />
            </Box>
          )}
        </React.Fragment>
      </ErrorBoundary>
    </div>
  )
}

Navigation.propTypes = {
  skipNavId: PropTypes.string,
  hasCollegesToCompare: PropTypes.bool,
  location: PropTypes.object,
}

export default connect((store) => {
  return {
    hasCollegesToCompare: size(store.collegeCompare) > 0,
  }
})(Navigation)
